<template>
    <v-container fluid>
        <v-card class="pa-2 ma-4"
                :loading="headerLoading">
            <v-row>
                <v-col v-if="header!=null">
                    <div class="headline mb-1">{{header.orderSubType}}</div>&nbsp;
                    <div class="caption">({{header.orderCode}})</div>&nbsp;
                </v-col>
                <v-col v-if="currentOrderState!=null">
                    <div class="font-weight-bold">{{currentOrderState.name}}</div>
                    <div class="caption">{{currentOrderState.description}}</div>
                </v-col>
                <v-card-actions v-for="orderStateOption in currentOrderStateOptions" :key="orderStateOption.id">
                    <v-btn color="primary"
                           dark
                           :disabled="!canClose || !hasAccess(getAccessType(orderStateOption.nextOrderStateId))"
                           @click="changeOrderState(orderStateOption.nextOrderStateId)">
                        {{ orderStateOption.name }} <v-icon color="white" small>{{orderStateOption.icon}}</v-icon>
                    </v-btn>       
                </v-card-actions>
              <v-col>
                <v-btn v-if="currentOrderState!=null && currentOrderState.isClosed"
                       color="primary"
                       dark
                       class="mt-2"
                       @click="download(header.id, `order_${header.originId}_${header.xId}`)">
                  Download File <v-icon color="white" small>mdi-download</v-icon>
                </v-btn>
              </v-col>
                <v-col class="px-8"
                       align="right"
                       justify="end">
                    <v-icon v-if=header.isComplete class="mr-1">mdi-package-variant-closed</v-icon>
                    <v-icon v-else class="mr-1">mdi-package-variant</v-icon>
                    <span class="subheading mr-2">{{ getOrderTotals().count }}</span>
                    <span class="mr-1">·</span>
                    <v-icon class="mr-1">mdi-cash-multiple</v-icon>
                    <span class="subheading">{{ formatCurrency(getOrderTotals().total, true) }}</span>
                </v-col>
            </v-row>
            <ModalDialog :item="deletedItem"
                         headline="Delete Item"
                         message="This will delete the item from the Requisition Order. Would you like to continue?"
                         :onAccept="deleteItem"
                         :dialogProxy="deleteDialog" />
        </v-card>

        <v-data-table class="elevation-1 pa-2 ma-4"
                      fixed-header
                      fixed-footer
                      height="600px"
                      :items="items"
                      :headers="filteredHeaders"
                      :loading="itemsLoading"
                      :server-items-length="totalItems"
                      :options.sync="options"
                      :footer-props="footerProps"
                      group-by="locationDepartmentId"
                      show-expand
                      dense
                      :single-expand=true
                      :expanded.sync="expanded">

            <template v-slot:[`header.materialXId`]="{ header }">
                <v-menu offset-y open-on-hover>
                    <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">{{ header.text }}</span>
                        <v-icon x-small
                                color="secondary"
                                v-bind="attrs"
                                v-on="on">mdi-dots-vertical</v-icon>
                    </template>
                    <v-list>
                        <v-list-item @click="toggleGroup">
                            <v-icon left small>mdi-unfold-more-horizontal</v-icon>Toggle Groups
                        </v-list-item>
                        <v-list-item @click="expandAll">
                            <v-icon left small>mdi-expand-all-outline</v-icon>Expand All
                        </v-list-item>
                        <v-list-item @click="collapseAll">
                            <v-icon left small>mdi-collapse-all-outline</v-icon>Collapse All
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>

            <template v-slot:[`header.quantity`]="{ header }">
                <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">{{ header.text }}</span>
                        <v-icon x-small
                                color="secondary"
                                v-bind="attrs"
                                v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Quantity of the specified material for a given department.</span>
                </v-tooltip>
            </template>

            <template v-slot:[`group.header`]="props">
                <td :colspan="3">
                    <v-btn icon v-on:click="props.toggle" tabindex="-1" :ref="props.group" :data-open="props.isOpen">
                        <v-icon v-if="!props.isOpen">mdi-plus</v-icon>
                        <v-icon v-if="props.isOpen">mdi-minus</v-icon>
                    </v-btn>
                    <v-icon v-if="findDepartmentTotal(props.group).isComplete" dark color="success">mdi-check-bold</v-icon>
                    <v-icon dark color="secondary" v-else>mdi-sync</v-icon>
                    &nbsp;
                    {{findDepartmentTotal(props.group).departmentName}}
                </td>

                <td :colspan="3">
                    <v-chip class="subheading mr-2" small>
                        <v-icon v-if="findDepartmentTotal(props.group).isComplete" class="mr-1">mdi-package-variant-closed</v-icon>
                        <v-icon v-else class="mr-1">mdi-package-variant</v-icon>
                        <div style="min-width:35px;text-align:right">
                            {{getDepartmentTotals(props.group).count}}
                        </div>
                    </v-chip>
                    <v-chip v-if="getVrsUser.showPricing" class="subheading mr-2" small> <v-icon class="mr-1">mdi-cash-multiple</v-icon><div style="min-width:50px;text-align:right">{{formatCurrency(getDepartmentTotals(props.group).total,false)}}</div></v-chip>
                    <v-btn 
                        v-if="!findDepartmentTotal(props.group).isComplete" 
                        :disabled="!(hasAccess('COMPLETEDEPTRO') && $route.params.locationDepartmentId == props.group) && !hasAccess('COMPLETERO')"
                        class="primary" 
                        x-small 
                        @click="submitDepartment(props.group, true)">
                        <v-icon class="text-sm-button">mdi-check</v-icon>Submit Department 
                    </v-btn>
                    <v-btn 
                        v-if="findDepartmentTotal(props.group).isComplete && header.orderStateId == 'OPEN'" 
                        :disabled="!(hasAccess('REOPENDEPTRO') && $route.params.locationDepartmentId == props.group)"
                        class="red darken-1 white--text" 
                        x-small 
                        @click="submitDepartment(props.group, false)">
                        <v-icon class="text-sm-button">mdi-close-circle-outline</v-icon>Cancel Department
                    </v-btn>
                </td>

                <td v-if="getVrsUser.showPricing"> <div class="font-weight-bold text-right justify-center">{{formatCurrency(getDepartmentTotals(props.group).total,false)}}</div></td>

            </template>

            <template v-slot:[`item.materialXId`]="{ item }">
                <span v-if="item.id > 0" :class="{'error--text':itemHasError(item)}">{{item.materialXId}}</span>
                <span v-if="item.id < 0">No Items</span>
            </template>

            <template v-slot:[`item.data-table-select`]="" class="btn">
            </template>

            <template v-slot:[`item.quantity`]="{ item }">
                <QuantityInput v-model.number="item.quantity"
                               :rules="[rules.positiveInteger]"
                               :disabled="item.isBusy || !canEdit || item.locationDepartmentId != $route.params.locationDepartmentId"
                               :update="onQuantityChange"
                               :el="generateElementName"
                               v-bind:ref="generateElementName(item, 'quantityElement')"
                               :onMoveNext="nextItem"
                               :onMovePrevious="previousItem"
                               :item="item"
                               :errorMessages="item.quantityError"
                               v-if="item.id > 0">
                </QuantityInput>
            </template>

            <template v-slot:[`item.materialDescription`]="{ item }">
                <span :class="{'error--text':itemHasError(item)}"><span v-if="item.materialAlias != null" class="font-weight-bold">({{ item.materialAlias }})&nbsp;</span>{{ item.materialDescription }}</span>

                <span>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-icon small v-on="on" v-show="item.hasNote==true" color="orange" class="ml-5">mdi-note-multiple-outline</v-icon>
                        </template>
                        <span>{{item.notes}}</span>
                    </v-tooltip>
                </span>
                <span v-for="(itm) in item.attributes" :key="itm.requisitionOrderItemAttributeCodeId" class="float-right">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-icon medium v-on="on" v-show="itm.isSelected" v-bind:color="itm.color">{{itm.icon}}</v-icon>
                        </template>
                        <span>{{itm.attributeDescription}}</span>
                    </v-tooltip>
                </span>
            </template>

            <template v-slot:[`item.unitPrice`]="{ item }">
                <UnitPriceInput
                    v-model.number="item.unitPrice"
                    :rules="[rules.positiveInteger]"
                    :disabled="item.isBusy || !canEdit || item.locationDepartmentId != $route.params.locationDepartmentId"
                    :update="onPriceChange"
                    :el="generateElementName"
                    v-bind:ref="generateElementName(item, 'priceElement')"
                    :onMoveNext="nextItem"
                    :onMovePrevious="previousItem"
                    :item="item"
                    :errorMessages="item.priceError"
                    v-if="item.id > 0"
                >
                </UnitPriceInput>
            </template>

            <template v-slot:[`item.totalPrice`]="{ item }">
                <div class="text-right align-center">{{ formatCurrency(item.totalPrice, false) }}</div>
            </template>
            <template v-slot:[`header.totalPrice`]="{ header }">
                <div class="text-right justify-center">{{ header.text }}</div>
            </template>
            <template v-slot:[`header.unitPrice`]="{ header }">
                <div class="text-left justify-center">{{ header.text }}</div>
            </template>
            <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
                <v-btn icon @click="expand(true)" v-if="item.id > 0 && !isExpanded"><v-icon>mdi-chevron-down</v-icon></v-btn>
                <v-btn icon @click="expand(false)" v-if="item.id > 0 && isExpanded"><v-icon>mdi-chevron-up</v-icon></v-btn>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
                <td v-bind:colspan="headers.length">
                    <RequisitionOrderItemForm v-if="item.id > 0" :item="item" :requisitionOrder="currentOrderState" :onDelete="onItemDeleted" />
                </td>
            </template>
        </v-data-table>
    </v-container>
</template>
<script>
    import QuantityInput from '@/components/QuantityInput.vue';
    import UnitPriceInput from '@/components/UnitPriceInput.vue';
    import RequisitionOrderItemForm from './RequisitionOrderItemForm.vue';
    import ModalDialog from '@/components/ModalDialog.vue';
    import { mapGetters } from 'vuex';
    import axios from "axios";

    export default {
        data() {
            return {
                header: {},
                headerLoading: true,
                trips: [],
                orderStates: [],
                currentOrderState: {},
                currentOrderStateOptions: [],
                items: [],
                departmentSummary: [],
                expanded: [],
                expanding: false,
                totalItems: 0,
                itemsLoading: false,
                deleteDialog: false,
                deletedItem: {},
                options: {},
                rules: {
                    positiveInteger: value => !isNaN(parseInt(value)) >= 0 || 'Positive value required.',
                },
                headers: [
                    { text: "Material ID", value: "materialXId", align: "start", groupable: false, width: '8%', sortable: false },
                    { text: "Quantity", value: "quantity", groupable: false, sortable: false, width: '8%' },
                    { text: "Unit", value: "uomId", groupable: false, sortable: false, width: '4%' },
                    { text: "Name", value: "materialDescription", groupable: false, sortable: false, width: '72%' },
                    { text: "Unit Price", value: "unitPrice", groupable: false, sortable: false, width: '8%' },
                    { text: "Total Price", value: "totalPrice", groupable: false, sortable: false },
                    { text: "Department ID", value: "locationDepartmentId", groupable: false, sortable: false }
                ],
                props: ["onDelete"],
                footerProps: {
                    'items-per-page-options': [-1]
                },
            }
        },
        components: {
            RequisitionOrderItemForm,
            QuantityInput,
            ModalDialog,
            UnitPriceInput
        },
        created() {
            if (this.$route.params.id == null || this.$route.params.id == -1 || this.$route.params.id == 0) {
                this.$router.push({
                    name: "orderhistory",
                    params: { locationId: this.$route.params.locationId || this.getVrsUser.locationId }
                });
            }
            //Avoid multiple mappings
            if (this.$route.params.locationDepartmentId == 0 || this.$route.params.locationDepartmentId == 'null'){
                this.$router.push({ 
                    name: "requisitionorder", 
                    params: { 
                        locationId: this.$route.params.locationId, 
                        locationDepartmentId: this.$route.params.locationId + this.getVrsUser.departmentId, 
                        id: this.$route.params.id
                    }
                }).catch((e) => console.log(e));
            }
            this.$eventHub.$off("order-updated", this.onOrderUpdated);
            this.$eventHub.$on("order-updated", this.onOrderUpdated);
        },

        beforeDestroy() {
            this.$eventHub.$off("order-updated", this.onOrderUpdated);
        },

        watch: {
            items: {
                handler() {
                    if (this.items.length === this.totalItems) {

                        this.items.forEach(item => {
                            if (item.locationDepartmentId in this.departmentSummary) {
                                this.departmentSummary[item.locationDepartmentId] += item.totalPrice;
                            } else {
                                this.departmentSummary[item.locationDepartmentId] = item.totalPrice
                            }
                        });
                    }
                }
            }
        },

        async mounted() {
            this.headerLoading = true;

            this.orderStates = await this.getOrderStates();
            this.trips = await this.getTrips();

            this.header = await this.getRequisitionOrder();

            if (this.header.scheduleId == null) {
                this.header.scheduleId = -1;
            }

            this.departmentSummary = await this.getRequisitionOrderSummary();           

            this.items = await this.getRequisitionOrderItems();
            this.itemsLoading = false;


            this.totalItems = this.items.length;
            this.currentOrderState = await this.resolveCurrentOrderState();
            this.currentOrderStateOptions = await this.resolveCurrentOrderStateOptions();

            this.headerLoading = false;
            if (this.currentOrderState.id == 'SEND') {
                this.submitCheck();
            }

        },

        computed: {
            ...mapGetters(['hasAccess', 'getVrsUser']),
            canClose() {
                var close = true;

                this.departmentSummary.forEach((dept) => {
                    close = close && dept.isComplete;
                });

                return close;
            },
            filteredHeaders() {
                if (!this.getVrsUser.showPricing){
                    return this.headers.filter(header => header.value != 'unitPrice' && header.value != 'totalPrice');
                }
                return this.headers;
            },
            canEdit() {
                return (
                        (this.currentOrderState.isOpen && (this.hasAccess('MATERIALRO'))) || 
                        (this.currentOrderState.isProcessing && (this.hasAccess('PROCESSRO')))
                    ) 
            },
        },

        methods: {
            async onOrderUpdated() {
                this.itemsLoading = true;
                this.items = await this.getRequisitionOrderItems();
                this.totalItems = this.items.length;
                this.itemsLoading = false;
            },

            async getOrderStates() {
                let response = await axios.get(`/api/Lookup/orderstates`);
                return response.data;
            },

            async getRequisitionOrder() {
                let response = await axios.get(`/api/RequisitionOrder/${this.$route.params.id}`);
                return response.data;
            },

            resolveCurrentOrderState() {
                return this.orderStates.find(e => e.id == this.header.orderStateId);
            },

            resolveCurrentOrderStateOptions() {
                return this.currentOrderState.orderStateOptions;
            },

            async getTrips() {
                var locationId = this.$route.params.locationId;

                let response = await axios.get(`/api/schedule/location/${locationId}?PageNumber=1&PageSize=1000`);
                let data=response.data;
                data.unshift({
                    id: -1,
                    name: "UNASSIGNED",
                    locationId: "",
                    formattedStartDate: "",
                    startDate: "",
                    endDate: "",
                    formattedEndDate: "",
                });

                return data;
            },

            async getRequisitionOrderSummary() {
                let response = await axios.get(`/api/RequisitionOrder/requisitionorderid/${this.$route.params.id}/requisitionorderdepartments`);
                return response.data;
            },

            async getRequisitionOrderItems() {
                this.itemsLoading = true;
                var locationId = this.$route.params.locationId;
                const { sortBy, sortDesc, page, itemsPerPage } = this.options;

                let response = await axios.get(`/api/RequisitionOrder/requisitionorderid/${this.$route.params.id}/requisitionOrderitems?PageNumber=${page}&PageSize=${itemsPerPage}`);
                let data=response.data;

                var x = 0;

                data.forEach((item) => {

                    item.isBusy = false;
                    item.quantityIsBusy = false;
                    item.materialIsBusy = false;
                    item.noteIsBusy = false;
                    item.adHocDescriptionIsBusy = '';
                    item.sequence = x++;
                    item.quantityError = '';
                    item.materialError = '';
                    item.noteError = '';
                    item.attributeError = '';
                    item.adHocDescriptionError = '';
                    item.hasNote = (item.notes && item.notes.length > 0) == true;
                    item.originalQuantity = item.quantity;
                });

                return data;
            },

            async apiRoPut(item) {
                item.errorMessage = '';
                item.hasError = false;
                try{
                    let response = await axios.put(`/api/RequisitionOrder`,item);
                    return response.data;
                }
                catch (error) {
                    item.errorMessage = 'An unknown network has occurred.';
                    item.hasError = true;

                    return item;
                }
            },

            async apiRoDepartmentPut(item) {
                item.errorMessage = '';
                item.hasError = false;

                try{
                    let response = await axios.put(`/api/RequisitionOrder/departmentstate`,item);
                    return response.data;
                }
                catch (error) {
                    item.errorMessage = 'An unknown network has occurred.';
                    item.hasError = true;

                    return item;
                }
            },

            async apiRoItemPut(item) {
                item.errorMessage = '';
                item.hasError = false;

                try{
                    let response = await axios.put(`/api/RequisitionOrder/subtotal`,item);
                    let data=response.data;
                    data.isBusy = false;
                    data.quantityIsBusy = false;
                    data.materialIsBusy = false;
                    data.noteIsBusy = false;
                    data.adHocDescriptionIsBusy = '';
                    data.sequence = item.sequence;
                    data.quantityError = '';
                    data.priceError = '';
                    data.materialError = '';
                    data.noteError = '';
                    data.attributeError = '';
                    data.adHocDescriptionError = '';
                    data.hasNote = (data.notes && data.notes.length > 0) == true;
                    data.originalQuantity = item.quantity;
                    return response.data;
                }
                catch (error) {
                    item.errorMessage = 'An unknown network has occurred.';
                    item.hasError = true;

                    return item;
                }
            },

            getOrderTotals() {
                var x = 0;
                var t = 0;

                this.items.forEach((item) => {
                    //Filter out Dept Stub Records
                    if (item.materialId != -1) {
                        x++;
                        t = t + item.totalPrice;
                    }

                });

                return { count: x, total: t };
            },

            getDepartmentTotals(deptId) {
                var x = 0;
                var t = 0;

                this.items.forEach((item) => {
                    if (item.locationDepartmentId == deptId && item.materialId != -1) {
                        x++;
                        t = t + item.totalPrice;
                    }
                });

                return { count: x, total: t };
            },

            findDepartmentTotal(deptId) {
                var deptTotal = this.departmentSummary.find(d => d.locationDepartmentId === deptId);

                if (!deptTotal) {
                    return { isComplete: true, departmentName: deptId + ' Not Found' };
                }

                return deptTotal;
            },

            itemHasError(item) {
                { return item.quantityError.length > 0 || item.materialError.length > 0 || item.noteError.length > 0 || item.attributeError.length > 0; }
            },

            formatCurrency(price, round) {
                let formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                });
                if (round) return formatter.format(Math.round(price, 0)).split('.')[0];
                return formatter.format(price);
            },

            generateElementName(item, prefix) {
                return prefix + item.sequence;
            },

            deleteItem(item, accept) {
                if (accept) {
                    item.quantity = 0;
                    this.quantityUpdate(item);
                } else {
                    item.quantity = item.originalQuantity;
                }
                this.deleteDialog = false;
            },

            async onQuantityChange(item, previousValue) {
                if (!item.quantity) {
                    item.quantity = 0;
                }

                if (item.quantity == previousValue) {
                    return;
                }

                if (item.quantity < 0) {
                    item.quantityError = 'Only positive values are allowed.';
                    item.quantity = previousValue;
                    return;
                }

                if (item.quantity == 0) {
                    this.deletedItem = item;
                    this.deleteDialog = true;
                    return;
                }

                await this.quantityUpdate(item, previousValue);
            },

            async onPriceChange(item, previousValue) {
                if (!item.unitPrice) {
                    item.unitPrice = 0;
                }

                if (item.unitPrice == previousValue) {
                    return;
                }

                if (item.unitPrice < 0) {
                    item.priceError = 'Price must be equals or greater than $0.00.';
                    item.unitPrice = previousValue;
                    return;
                }
                await this.priceUpdate(item, previousValue);
            },

            async quantityUpdate(item, previousValue) {
                const index = this.items.indexOf(item);

                //NOTE: Setting attributes on item will also affect the detail form
                item.quantityError = '';
                item.errorMessage = null;
                var result = await this.apiRoItemPut(item);

                if (result.hasError) {
                    item.errorMessage = "An error occurred. Item was not updated.";
                    item.quantityError = "An error occurred. Item was not updated.";
                    item.quantity = previousValue;
                } else {
                    item = result;
                }

                item.quantityIsBusy = false;

                item.totalPrice = item.quantity * item.unitPrice;

                if (item.quantity == 0) {

                    if (index > -1) {
                        this.items.splice(index, 1);
                    }
                }
            },

            async priceUpdate(item, previousValue) {
                item.quantityError = '';
                item.errorMessage = null;
                let  result = await this.apiRoItemPut(item);

                if (result.hasError) {
                    item.errorMessage = "An error occurred. Item was not updated.";
                    item.priceError = "An error occurred. Item price was not updated.";
                    item.unitPrice = previousValue;
                } else {
                    item = result;
                }

                item.priceIsBusy = false;

                item.totalPrice = item.quantity * item.unitPrice;
            },

            async onItemDeleted(item) {
                //This is called from the ItemForm to notify the table to remove the deleted row
                const index = this.items.indexOf(item);
                if (index > -1) {
                    this.items.splice(index, 1);
                }
            },

            toggleGroup() {
                Object.keys(this.$refs).forEach(k => {
                    this.$refs[k].$el.click();
                });
            },

            collapseAll() {
                Object.keys(this.$refs).forEach(k => {
                    if (this.$refs[k] && this.$refs[k].$attrs["data-open"]) {
                        this.$refs[k].$el.click();
                    }
                });
            },

            expandAll() {
                Object.keys(this.$refs).forEach(k => {
                    if (this.$refs[k] && !this.$refs[k].$attrs["data-open"]) {
                        this.$refs[k].$el.click();
                    }
                });
            },

            async submitDepartment(locationDepartmentId, isComplete) {
                var dept = this.findDepartmentTotal(locationDepartmentId);
                dept.isComplete = isComplete;
                dept = await this.apiRoDepartmentPut(dept);
            },

            async submitCheck() {
                for (const dept of this.departmentSummary) {
                    var d = this.findDepartmentTotal(dept.locationDepartmentId);
                    if (!d.isComplete) {
                        d.isComplete = true;
                        d = await this.apiRoDepartmentPut(d);
                    }
                }
            },

            async changeOrderState(orderStateId) {
                this.header.orderStateId = orderStateId;

                try{
                    let response = await axios.put(`/api/RequisitionOrder`,this.header);
                    this.currentOrderState = this.resolveCurrentOrderState();
                    this.currentOrderStateOptions = this.resolveCurrentOrderStateOptions();
                    return response.data;
                }
                catch (error) {
                    this.header.errorMessage = 'An unknown network has occurred.';
                    this.header.hasError = true;

                    return this.header;
                }
            },

            nextItem(item, prefix) {
                const index = prefix + (item.sequence + 1);
                if (this.$refs[index]) {
                    this.$refs[index].focus(index);
                } else {
                    this.$refs[prefix + (item.sequence)].blur(prefix + (item.sequence));
                }
            },

            previousItem(item, prefix) {
                const index = prefix + (item.sequence - 1);
                if (this.$refs[index]) {
                    this.$refs[index].focus(index);
                } else {
                    this.$refs[prefix + (item.sequence)].blur(prefix + (item.sequence));
                }
            },
          async download(orderId, filename) {
              let response = await axios.get(`api/RequisitionOrder/${orderId}/file`);
              const blob = new Blob([response.data],{type: response.headers["content-type"]})
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download=filename;
              link.click();
              URL.revokeObjectURL(link.href);
          },

            getAccessType(action) {
                let result = 'NOACCESS'
                switch(action) {
                    case 'SEND': 
                        result = 'COMPLETERO'
                        break;
                    case 'PROCESS': 
                        result = 'PROCESSRO'
                        break;
                    case 'OPEN': 
                        result = 'RETURNRO'
                        break;
                    case 'REVIEW': 
                        result = 'PROCESSRO'
                        break;
                    case 'SUBMIT': 
                        result = 'SUBMITRO'
                        break;
                    case 'CLOSED': 
                        result = 'SUBMITRO'
                        break;
                }
                return result;
            }
        }
    }
</script>
